import { useState } from 'react';
import { toast } from '@pella/sash-framework';
import { useTranslation } from 'react-i18next';
import { useAPI } from '@pella/cosmos-react';
import { useThrive } from '../useThrive';
import {
  SubmitTeamsUserMsg,
  SubmitTeamsChannelCard,
  SubmitTeamsChannelCardCWS,
  SubmitTeamsUserCard,
  SubmitTeamsChannelCardAvanti,
  SubmitTeamsUserCardCWS,
  SubmitTeamsUserCardAvanti,
} from '../../api/teamsDataActions';
import siteDetails from '../../data/SiteDetails';
import { sendTeamsMessages } from '../../data/configure';
import { useCreateConversation, useCreateWorkOrderTracking, useGetTranslation } from '../useMaintenanceCopilot';
import { followUpQuestionsToQuestionAnswers } from '../../utils/functions';

// The production hostname branch cannot be covered in a jsdom test environment.
/* istanbul ignore next */
const LeanTechAppUrl =
  window.location.hostname === 'equipment-action-portal.pella.com'
    ? 'https://thrive.pella.com'
    : 'https://thrivetest.pella.com';

const getReadableError = (error) => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || 'Unknown error';

  if (typeof error === 'object') {
    const typedError = /** @type {{ message?: unknown, error?: unknown, data?: unknown }} */ (error);

    if (typeof typedError.message === 'string' && typedError.message.trim()) {
      return typedError.message;
    }

    if (typeof typedError.error === 'string' && typedError.error.trim()) {
      return typedError.error;
    }

    if (typedError.data) {
      return getReadableError(typedError.data);
    }

    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown error';
    }
  }

  return String(error);
};

/**
 * Hook to manage work order submission flow including conversation creation, downtime events, and Teams notifications.
 * @param {object} root0 - Configuration object
 * @param {object} [root0.selectedEquipment] - The selected equipment data
 * @param {object} [root0.serviceAreaSelected] - The selected service area
 * @param {object} [root0.serviceActionSelected] - The selected service action
 * @param {object} [root0.employeeDetails] - Employee details from the API
 * @param {string} [root0.comment] - User comment about the issue
 * @param {boolean} [root0.isMaintenanceCopilotFlagEnabled] - Whether maintenance copilot feature is enabled
 * @param {() => void} [root0.onSuccess] - Callback when submission succeeds
 * @param {(description: string | undefined) => void} [root0.onConversationCreated] - Callback when conversation is created
 * @returns {object} Hook result with handlers and state
 */
export const useWorkOrderSubmit = ({
  selectedEquipment,
  serviceAreaSelected,
  serviceActionSelected,
  employeeDetails,
  comment,
  isMaintenanceCopilotFlagEnabled,
  onSuccess,
  onConversationCreated,
}) => {
  const { t, i18n } = useTranslation();
  const userLanguage = i18n.language?.slice(0, 2);
  const { api } = useAPI();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [summaryText, setSummaryText] = useState('');
  const [, setSummaryGenerationError] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [downtimeStartTime, setDowntimeStartTime] = useState(null);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryDialogMessage, setRetryDialogMessage] = useState('');

  const { useStartDowntimeEvent } = useThrive();
  const { startDowntimeEvent } = useStartDowntimeEvent();

  const { mutate: createConversation, isPending: loadingCreateConversation } = useCreateConversation({
    onSuccess: (data) => {
      // Loading bar hidden by TanStack Query GlobalLoading
      setConversationId(data?.id);
      onConversationCreated?.(data?.problemDescription);
    },
    onError: (err) => {
      // Loading bar hidden by TanStack Query GlobalLoading
      setRetryDialogMessage(t('conversationErrorContent', { error: err?.message }));
      setRetryDialogOpen(true);
    },
  });

  const { mutateAsync: getTranslation } = useGetTranslation();
  const { mutate: createTracking } = useCreateWorkOrderTracking();

  const sendTeamsResponseToTech = (wo, tech) => {
    if (!sendTeamsMessages) return;
    const maintTechUserCard = {
      type: 'AdaptiveCard',
      version: '1.3',
      sendTo: `${tech.usr_login}@pella.com`,
      title: 'Downtime Response Event',
      body: [
        {
          type: 'Container',
          items: [
            {
              type: 'TextBlock',
              text: `Maintenance ${serviceActionSelected.item} Event`,
              size: 'medium',
              weight: 'bolder',
              wrap: 'true',
            },
            {
              type: 'TextBlock',
              spacing: 'none',
              weight: 'lighter',
              text: `_Created ${new Date().toLocaleString()}_`,
              isSubtle: 'true',
              wrap: 'true',
            },
            {
              type: 'TextBlock',
              text: `Assigned To: ${tech.first_name}${tech.preferred_name ? `(${tech.preferred_name})` : ''} ${tech.last_name}`,
              spacing: 'none',
              wrap: 'true',
            },
            {
              type: 'TextBlock',
              text: `Submitted by: ${employeeDetails.first_name}${employeeDetails.preferred_name ? `(${employeeDetails.preferred_name})` : ''} ${employeeDetails.last_name}`,
              wrap: 'true',
            },
            { type: 'TextBlock', spacing: 'none', text: `WO: ${wo?.projNum}`, wrap: 'true' },
            { type: 'TextBlock', spacing: 'none', text: `Dept: ${selectedEquipment?.departmentId}`, wrap: 'true' },
            { type: 'TextBlock', spacing: 'none', text: `Location: ${selectedEquipment?.location}`, wrap: 'true' },
            { type: 'TextBlock', spacing: 'none', text: `Equipment#: ${selectedEquipment?.equipNum}`, wrap: 'true' },
            {
              type: 'TextBlock',
              spacing: 'none',
              text: `Description: ${selectedEquipment?.description}`,
              wrap: 'true',
            },
            {
              type: 'TextBlock',
              spacing: 'none',
              text: `Reason For Outage: ${serviceActionSelected.item}`,
              wrap: 'true',
            },
            { type: 'TextBlock', spacing: 'none', text: `Comment: ${comment}`, wrap: 'true' },
          ],
        },
      ],
      actions: [
        {
          type: 'Action.OpenUrl',
          title: 'Go to Thrive Work Orders',
          url: `${LeanTechAppUrl}/3/CSTM/MT/Mobile?tbl_id=${wo.id}`,
        },
        {
          type: 'Action.OpenUrl',
          title: 'Go to Thrive RTS Dashboard',
          url: `${LeanTechAppUrl}/3/CSTM/MT/Dashboard2?retries=1`,
        },
      ],
    };
    if (wo.siteId === 'CWS') {
      SubmitTeamsUserCardCWS(maintTechUserCard);
    } else if (wo.siteId === 'Avanti') {
      SubmitTeamsUserCardAvanti(maintTechUserCard);
    } else {
      SubmitTeamsUserCard(maintTechUserCard);
    }
  };

  const sendTeamsResponseToAuthor = (wo) => {
    if (!employeeDetails || !selectedEquipment || !sendTeamsMessages) return;
    /* istanbul ignore else -- only called from addWorkOrder which gates on Maintenance */
    if (serviceAreaSelected.serviceArea === 'Maintenance') {
      const maintChatMsg = {
        chatInfo: {
          sendTo: `${employeeDetails.usr_login}@pella.com`,
          title: `${serviceAreaSelected.serviceArea} ${serviceActionSelected.item} Event Created`,
          message: `Created ${new Date().toLocaleString()}</br>Equipment#: ${selectedEquipment?.equipNum} Description: ${selectedEquipment?.description}</br>WO #: ${wo?.projNum}`,
        },
      };
      if (wo.siteId === 'CWS' || wo.siteId === 'Medley') {
        // SubmitTeamsUserMsg(maintChatMsg);
      } else if (wo.siteId === 'Avanti') {
        // SubmitTeamsUserMsg(maintChatMsg);
      } else {
        SubmitTeamsUserMsg(maintChatMsg);
      }
    }
    // Quality author message — only reachable when serviceArea is 'Quality', but sendTeamsResponseToAuthor
    // is only called from addWorkOrder which gates on serviceArea === 'Maintenance'.
    /* istanbul ignore next */
    if (serviceAreaSelected.serviceArea === 'Quality') {
      const qualityChatMsg = {
        chatInfo: {
          sendTo: `${employeeDetails.usr_login}@pella.com`,
          title: 'Quality Downtime Event Created',
          message: `Created ${new Date().toLocaleString()}</br>Equipment#: ${selectedEquipment?.equipNum} Description: ${selectedEquipment?.description}`,
        },
      };
      if (wo.siteId === 'CWS' || wo.siteId === 'Medley') {
        // SubmitTeamsUserMsg(qualityChatMsg);
      } else {
        SubmitTeamsUserMsg(qualityChatMsg);
      }
    }
  };

  const sendTeamsMessage = (wo) => {
    if (!sendTeamsMessages) return;
    const siteTeamsInfo = siteDetails.find((e) =>
      e.departmentId
        ? e.siteId.toUpperCase() === wo.siteId.toUpperCase() &&
          e.departmentId.toUpperCase() === selectedEquipment.departmentId.toUpperCase()
        : e.siteId.toUpperCase() === wo.siteId.toUpperCase(),
    );
    if (!siteTeamsInfo) {
      toast.warning(
        `Teams notification not sent: no configuration found for site "${wo.siteId}"${selectedEquipment.departmentId ? ` / department "${selectedEquipment.departmentId}"` : ''}.`,
      );
      return;
    }
    /* istanbul ignore else -- only called from addWorkOrder which gates on Maintenance */
    if (serviceAreaSelected.serviceArea === 'Maintenance') {
      const maintCardChannelMsg = {
        type: 'AdaptiveCard',
        version: '1.3',
        sendTo: {
          team: siteTeamsInfo.teamsConfig.maintenance.teamsAddress,
          channel: siteTeamsInfo.teamsConfig.maintenance.channelId,
        },
        title: 'Maintenance Event',
        body: [
          {
            type: 'Container',
            items: [
              {
                type: 'TextBlock',
                text: `Maintenance ${serviceActionSelected.item} Event`,
                size: 'medium',
                weight: 'bolder',
                wrap: 'true',
              },
              {
                type: 'TextBlock',
                spacing: 'none',
                weight: 'lighter',
                text: `_Created ${new Date().toLocaleString()}_`,
                isSubtle: 'true',
                wrap: 'true',
              },
              {
                type: 'TextBlock',
                text: `Submitted by: ${employeeDetails.first_name}${employeeDetails.preferred_name ? `(${employeeDetails.preferred_name})` : ''} ${employeeDetails.last_name}`,
                wrap: 'true',
              },
              { type: 'TextBlock', spacing: 'none', text: `WO: ${wo?.projNum}`, wrap: 'true' },
              { type: 'TextBlock', spacing: 'none', text: `Dept: ${selectedEquipment?.departmentId}`, wrap: 'true' },
              { type: 'TextBlock', spacing: 'none', text: `Location: ${selectedEquipment?.location}`, wrap: 'true' },
              { type: 'TextBlock', spacing: 'none', text: `Equipment#: ${selectedEquipment?.equipNum}`, wrap: 'true' },
              {
                type: 'TextBlock',
                spacing: 'none',
                text: `Description: ${selectedEquipment?.description}`,
                wrap: 'true',
              },
              {
                type: 'TextBlock',
                spacing: 'none',
                text: `Reason For Outage: ${serviceActionSelected.item}`,
                wrap: 'true',
              },
              { type: 'TextBlock', spacing: 'none', text: `Comment: ${comment}`, wrap: 'true' },
            ],
          },
        ],
        actions: [
          {
            type: 'Action.OpenUrl',
            title: 'Go to Thrive Work Orders',
            url: `${LeanTechAppUrl}/3/CSTM/MT/Mobile?tbl_id=${wo.id}`,
          },
          {
            type: 'Action.OpenUrl',
            title: 'Go to Thrive RTS Dashboard',
            url: `${LeanTechAppUrl}/3/CSTM/MT/Dashboard2?retries=1`,
          },
        ],
      };
      if (wo.siteId === 'CWS' || wo.siteId === 'Medley') {
        SubmitTeamsChannelCardCWS(maintCardChannelMsg);
      } else if (wo.siteId === 'Avanti') {
        SubmitTeamsChannelCardAvanti(maintCardChannelMsg);
      } else {
        SubmitTeamsChannelCard(maintCardChannelMsg);
      }
    }
    // Quality channel card — only reachable when serviceArea is 'Quality', but sendTeamsMessage
    // with a valid wo is only called from addWorkOrder which gates on serviceArea === 'Maintenance'.
    /* istanbul ignore next */
    if (serviceAreaSelected.serviceArea === 'Quality') {
      const qualityCardChannelMsg = {
        type: 'AdaptiveCard',
        version: '1.3',
        sendTo: {
          team: siteTeamsInfo.teamsConfig.quality.teamsAddress,
          channel: siteTeamsInfo.teamsConfig.quality.channelId,
        },
        title: 'Quality Downtime Event',
        body: [
          {
            type: 'Container',
            items: [
              { type: 'TextBlock', text: 'Quality Downtime Event', size: 'medium', weight: 'bolder', wrap: 'true' },
              {
                type: 'TextBlock',
                spacing: 'none',
                weight: 'lighter',
                text: `_Created ${new Date().toLocaleString()}_`,
                isSubtle: 'true',
                wrap: 'true',
              },
              {
                type: 'TextBlock',
                text: `Submitted by: ${employeeDetails.first_name}${employeeDetails.preferred_name ? `(${employeeDetails.preferred_name})` : ''} ${employeeDetails.last_name}`,
                wrap: 'true',
              },
              {
                type: 'TextBlock',
                spacing: 'none',
                text: `Location: ${selectedEquipment?.departmentId}`,
                wrap: 'true',
              },
              { type: 'TextBlock', spacing: 'none', text: `Equipment#: ${selectedEquipment?.equipNum}`, wrap: 'true' },
              {
                type: 'TextBlock',
                spacing: 'none',
                text: `Description: ${selectedEquipment?.description}`,
                wrap: 'true',
              },
              {
                type: 'TextBlock',
                spacing: 'none',
                text: `Reason For Outage: ${serviceActionSelected.item}`,
                wrap: 'true',
              },
              { type: 'TextBlock', spacing: 'none', text: `Comment: ${comment}`, wrap: 'true' },
            ],
          },
        ],
      };
      if (wo.siteId === 'CWS' || wo.siteId === 'Medley') {
        SubmitTeamsChannelCardCWS(qualityCardChannelMsg);
      } else {
        SubmitTeamsChannelCard(qualityCardChannelMsg);
      }
    }
  };

  // summaryOverride defaults to current state only for the non-AI path where no explicit value is passed.
  // On that path summaryText is '' so memo03 is blank and tracking is skipped — correct behavior.
  const addWorkOrder = async (summaryOverride = summaryText) => {
    let commentTranslated;
    if (!isMaintenanceCopilotFlagEnabled && userLanguage === 'es') {
      const translationResult = await getTranslation([
        { sourceLanguage: 'SPANISH', targetLanguage: 'ENGLISH', textToTranslate: [{ id: 1, text: comment }] },
      ]);
      commentTranslated = translationResult?.[0]?.text;
    }

    const downtimeRequest = {
      Data: {
        callSource: 'Thrive-Downtime Event',
        equipNum: selectedEquipment.equipNum,
        requestedBy: employeeDetails?.usr_emp_no || 'Downtime Event',
        downtimeReason: serviceActionSelected.itemValue,
        taskTypeId: serviceActionSelected.itemValue === 'Planned Repair' ? 'Repair' : 'Breakdown',
        status: 'Executing',
        pickList06: serviceActionSelected.itemValue === 'Planned Repair' ? '' : 'Initiated',
        isDownTime: serviceActionSelected.itemValue === 'Planned Repair' ? 'N' : '1',
        comments: commentTranslated || comment,
        memo03: summaryOverride,
        utcOffset: new Date().getTimezoneOffset(),
        jsonInput: '',
        projectId: '',
        jsonOutput: '',
      },
    };

    try {
      const results = await startDowntimeEvent(downtimeRequest);
      if (results?.status === 201) {
        toast.success(
          t('downtimeEventStartedContent', {
            serviceArea: t(serviceAreaSelected.i18nTranslationKey),
            serviceAction: t(serviceActionSelected.i18nTranslationKey),
          }),
        );

        if (results.data.nextTech) {
          const tech = await api.teamMember.getTeamMemberByBadgeNumber(String(results.data.nextTech));
          if (tech) sendTeamsResponseToTech(results.data, tech);
        }
        sendTeamsResponseToAuthor(results.data);
        sendTeamsMessage(results.data);

        if (isMaintenanceCopilotFlagEnabled && summaryOverride) {
          createTracking({
            workOrderId: results.data.id,
            workOrderNumber: results.data.projNum,
            generatedSummary: summaryOverride,
            isRequestOpen: true,
            conversationId,
            questionAnswers: followUpQuestionsToQuestionAnswers(followUpQuestions),
            equipmentNumber: selectedEquipment.equipNum,
            siteId: selectedEquipment.siteId,
            downtimeStartTime,
          });
        }

        onSuccess?.();
      } else {
        toast.error(t('downtimeStartErrorContent', { error: getReadableError(results?.data || results) }));
      }
    } catch (error) {
      toast.error(t('downtimeStartErrorContent', { error: getReadableError(error) }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e, summaryOverride) => {
    e?.preventDefault();
    if (serviceAreaSelected && serviceActionSelected) {
      if (selectedEquipment && selectedEquipment !== '') {
        setIsSubmitting(true);
        if (serviceAreaSelected.serviceArea === 'Maintenance') {
          await addWorkOrder(summaryOverride);
        }
        if (serviceAreaSelected.serviceArea === 'Quality') {
          sendTeamsMessage();
          toast.info(t('qualityEventSentContent'));
          onSuccess?.();
          setIsSubmitting(false);
        }
        if (serviceAreaSelected.serviceArea === 'DeptManager') {
          toast.info(t('deptManagerSelectedContent'));
          onSuccess?.();
        }
      } else {
        toast.warning(t('equipmentMustBeSelectedContent'));
      }
    } else {
      toast.warning(t('downtimeEventMustBeSelectedContent'));
    }
  };

  /**
   * Called when AI summary generation succeeds. Sets summary text and triggers submit
   * with the fresh text value directly — avoids async state read timing issue.
   * @param {string} text - The generated summary text
   */
  const handleSummaryReady = (text) => {
    if (!text) return;
    setSummaryText(text);
    handleSubmit(undefined, text);
  };

  /**
   * Called when AI summary generation fails and the user dismisses the error.
   * Clears summary state and submits without a summary.
   */
  const handleSummaryError = () => {
    setSummaryText('');
    handleSubmit();
  };

  const handleCreateConversation = () => {
    // Loading bar shown by TanStack Query GlobalLoading
    setDowntimeStartTime(Date.now());
    createConversation({
      equipmentNumber: selectedEquipment.equipNum,
      problemDescription: comment,
      userLanguage,
    });
  };

  const handleRetryDialogClose = (shouldRetry) => {
    setRetryDialogOpen(false);
    if (shouldRetry) handleSubmit();
  };

  const reset = () => {
    setSummaryText('');
    setConversationId('');
    setDowntimeStartTime(null);
    setFollowUpQuestions([]);
    setSummaryGenerationError(false);
    setIsSubmitting(false);
    setRetryDialogOpen(false);
    setRetryDialogMessage('');
  };

  return {
    handleSubmit,
    handleCreateConversation,
    loadingCreateConversation,
    isSubmitting,
    conversationId,
    summaryText,
    handleSummaryReady,
    handleSummaryError,
    followUpQuestions,
    setFollowUpQuestions,
    downtimeStartTime,
    retryDialogOpen,
    retryDialogMessage,
    handleRetryDialogClose,
    reset,
  };
};
